/* eslint-disable @typescript-eslint/no-explicit-any, react-refresh/only-export-components */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Empty, FormChart, LineChart, Seg, Spark } from "./widgets";
import { IN, fiftyPlusSplit, batTotals, bowlTotals, dash, fmtDate, formatLabel, formatShort, fx, n0, nz, ovFromBalls, ballsFromOvers, resultKey, seasonSeries } from "./model";

const CHEV = (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const pctDiff = (a: number, b: number) => ((a - b) / b) * 100;
const Arrow = ({ d, lowerBetter }: { d: number | null; lowerBetter?: boolean }) => {
  if (d == null || !isFinite(d)) return null;
  const good = lowerBetter ? d <= 0 : d >= 0;
  return <span className={good ? "up" : "down"}>{d >= 0 ? "▲" : "▼"} {Math.abs(d).toFixed(1)}%</span>;
};

const fmtOptions = (rows: any[]): [string, string][] => [["all", "All formats"], ...Array.from(new Set(rows.filter((r) => n0(r.matchesPlayed) > 0).map((r) => r.format as string))).map((f) => [f, formatLabel(f)] as [string, string])];

export const useCareer = (overview: any) => {
  const bat = nz<any>(overview?.battingStats?.formatWiseStats).filter((r) => n0(r.matchesPlayed) > 0);
  const bowl = nz<any>(overview?.bowlingStats?.formatWiseStats).filter((r) => n0(r.matchesPlayed) > 0);
  return { bat, bowl, batAll: batTotals(bat), bowlAll: bowlTotals(bowl), bbi: overview?.bowlingStats?.bestBowlingFigures as string | undefined };
};

const MatchRow = ({ m, cols }: { m: any; cols: number }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const mm = m.matchMiniResponse ?? {};
  const bat = m.runsScored != null ? `${m.runsScored}${mm.notOut ? "*" : ""} (${m.ballsFaced ?? dash})` : dash;
  const bowl = m.wicketsTaken != null && m.oversBowled ? `${m.wicketsTaken}/${m.runsConceded ?? 0} (${m.oversBowled})` : dash;
  const rk = resultKey(m.matchResult);
  return (
    <>
      <tr className="click" tabIndex={0} aria-expanded={open} onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}>
        <td><div className="mtx"><b>vs {mm.opponentTeamName ?? dash}</b><span>{fmtDate(mm.matchDate)}</span></div></td>
        <td><span className="fmt">{formatShort(mm.matchType)}</span></td>
        <td>{bat}</td><td>{bowl}</td>
        <td><span className="rcell">{m.matchResult ? String(m.matchResult).replace(/_/g, " ").toLowerCase() : dash}<span className={`res ${rk}`}>{rk}</span></span></td>
        <td className="chev">{CHEV}</td>
      </tr>
      {open && (
        <tr className="det"><td colSpan={cols}>
          <div className="det-grid">
            <div>Match<b>{mm.playerTeamName} vs {mm.opponentTeamName}</b></div>
            <div>Competition<b>{mm.tournamentName ?? dash}</b></div>
            <div>Performance<b>{bat}{m.isPlayerOfMatch ? " · Player of the match" : ""}</b></div>
          </div>
          {mm.matchId && <div className="det-acts"><button className="btn" onClick={() => nav(`/matches-details/${mm.matchId}`)}>Full scorecard</button></div>}
        </td></tr>
      )}
    </>
  );
};

export const RecentTable = ({ list }: { list: any[] }) => (
  <div className="tw"><table>
    <thead><tr><th>Match</th><th>Format</th><th>Batting</th><th>Bowling</th><th>Result</th><th><span className="visually-hidden">Details</span></th></tr></thead>
    <tbody>{list.length ? list.map((m, i) => <MatchRow key={i} m={m} cols={6} />) : <tr><td colSpan={6} className="s" style={{ textAlign: "center", padding: 24 }}>No matches yet.</td></tr>}</tbody>
  </table></div>
);

const OverviewPane = ({ overview, onGo }: { overview: any; onGo: (t: string) => void }) => {
  const nav = useNavigate();
  const { bat, bowl, batAll, bowlAll, bbi } = useCareer(overview);
  const mini = nz<any>(overview?.tournamentMiniStats);
  const [batF, setBatF] = useState("all");
  const [batM, setBatM] = useState<"r" | "hs">("r");
  const [batSel, setBatSel] = useState<number | null>(null);
  const [bowlF, setBowlF] = useState("all");
  const [bowlSel, setBowlSel] = useState<number | null>(null);
  const [topF, setTopF] = useState("all");

  const bT = useMemo(() => batTotals(batF === "all" ? bat : bat.filter((r) => r.format === batF)), [bat, batF]);
  const bS = useMemo(() => seasonSeries(mini, batF).filter((x) => x.m > 0), [mini, batF]);
  const blT = useMemo(() => bowlTotals(bowlF === "all" ? bowl : bowl.filter((r) => r.format === bowlF)), [bowl, bowlF]);
  const blS = useMemo(() => seasonSeries(mini, bowlF).filter((x) => x.m > 0), [mini, bowlF]);
  const sel1 = batSel != null && batSel < bS.length ? batSel : bS.length - 1;
  const sel2 = bowlSel != null && bowlSel < blS.length ? bowlSel : blS.length - 1;
  const bVals = bS.map((x) => (batM === "r" ? x.r : x.hs));

  const recent = nz<any>(overview?.recentMatches);
  const formItems = recent.slice().reverse().map((m) => {
    const mm = m.matchMiniResponse ?? {};
    return { runs: n0(m.runsScored), notOut: !!mm.notOut, wk: n0(m.wicketsTaken), res: resultKey(m.matchResult), opp: mm.opponentTeamName ?? "–", label: `vs ${mm.opponentTeamName ?? "–"}, ${fmtDate(mm.matchDate)}: ${m.runsScored ?? 0} (${m.ballsFaced ?? 0})${m.oversBowled ? `, ${m.wicketsTaken ?? 0}/${m.runsConceded ?? 0}` : ""}` };
  });
  const fRuns = recent.reduce((a, m) => a + n0(m.runsScored), 0), fBalls = recent.reduce((a, m) => a + n0(m.ballsFaced), 0);
  const fOuts = recent.filter((m) => m.runsScored != null && !m.matchMiniResponse?.notOut).length;
  const fAvg = fOuts ? fRuns / fOuts : fRuns, fSr = fBalls ? (fRuns * 100) / fBalls : 0;
  const wins = recent.filter((m) => resultKey(m.matchResult) === "W").length, losses = recent.filter((m) => resultKey(m.matchResult) === "L").length;

  const topInn = nz<any>(overview?.topBattingInnings).filter((x) => topF === "all" || x.matchMiniResponse?.matchType === topF).slice(0, 3);
  const topSp = nz<any>(overview?.topBowlingInnings).slice(0, 3);
  const fast = nz<any>(overview?.topStrikeRateInnings);
  const miles = nz<any>(overview?.careerMilestones);
  const topFmts = Array.from(new Set(nz<any>(overview?.topBattingInnings).map((x) => x.matchMiniResponse?.matchType).filter(Boolean))) as string[];
  const hasBowl = bowlAll.w > 0 || bowlAll.balls > 0;

  const tile = (active: boolean, onClick: (() => void) | null, k: string, v: string, s: string, spark?: (number | null)[]) =>
    onClick ? (
      <button className="tile" aria-pressed={active} onClick={onClick}><span className="k">{k}</span><span className="v">{v}</span><span className="s">{s}</span>{spark && <Spark vals={spark} />}</button>
    ) : (
      <div className="tile"><span className="k">{k}</span><span className="v">{v}</span><span className="s">{s}</span></div>
    );
  const stat = (v: string | number, k: string, s?: string) => <div><div className="v">{v}</div><div className="k">{k}</div>{s && <div className="k">{s}</div>}</div>;
  const runT = Math.max(1000, Math.ceil((batAll.r + 1) / 500) * 500), wkT = Math.ceil((bowlAll.w + 1) / 10) * 10;
  const nextUps = [
    { k: `${IN(runT)} career runs`, v: batAll.r, t: runT, u: "run" },
    ...(hasBowl ? [{ k: `${wkT} career wickets`, v: bowlAll.w, t: wkT, u: "wicket" }] : []),
  ];

  return (
    <>
      {bat.length > 0 && (
        <Box title="Batting" sub={batF === "all" ? "Career, all formats" : `Career, ${formatLabel(batF)}`} right={<Seg options={fmtOptions(bat) as any} value={batF} onChange={(k) => { setBatF(k); setBatSel(null); }} />}
          footer={<><span>Select a stat or a season to explore.</span><button className="prolink" onClick={() => onGo("analysis")}>Advanced batting analysis</button></>}>
          <div className="bat-grid">
            <div className="tiles">
              {tile(batM === "r", () => setBatM("r"), "Runs", IN(bT.r), `${bT.inn} innings`, bS.map((x) => x.r))}
              {tile(false, null, "Average", fx(bT.avg), `${Math.max(0, bT.inn - bT.no)} dismissals`)}
              {tile(false, null, "Strike rate", fx(bT.sr, 1), "Runs per 100 balls")}
              {tile(batM === "hs", () => setBatM("hs"), "Highest score", String(bT.hs), "Tap to chart by season", bS.map((x) => x.hs))}
            </div>
            <div className="chartwrap">
              <div className="chart-head"><span className="lbl">{batM === "r" ? "Runs by season" : "Highest score by season"}</span></div>
              {bS.length ? <LineChart vals={bVals} labels={bS.map((x) => x.s)} sel={sel1} onSel={setBatSel} fmt={(v) => (batM === "r" ? `${IN(v)} runs` : `HS ${v}`)} tick={(v) => Math.round(v)} ref={batM === "r" ? bT.r / bS.length : null} refLabel="Season average" zero aria="Batting by season" /> : <Empty text="No season data yet." />}
              {bS[sel1] && <div className="cap"><b>{bS[sel1].s}</b>: {IN(bS[sel1].r)} runs in {bS[sel1].m} matches, highest score {bS[sel1].hs}. {sel1 > 0 && batM === "r" && bS[sel1 - 1].r > 0 ? <><Arrow d={pctDiff(bS[sel1].r, bS[sel1 - 1].r)} /> runs on {bS[sel1 - 1].s}</> : null}</div>}
            </div>
          </div>
          <div className="strip">{stat(bT.m, "Matches")}{stat(bT.inn, "Innings")}{stat(bT.no, "Not outs")}{stat(bT.h + bT.f, "50+ scores", fiftyPlusSplit(bT.h, bT.f))}{stat(`${bT.f4} / ${bT.s6}`, "Fours / sixes")}</div>
        </Box>
      )}

      {hasBowl && (
        <Box title="Bowling" sub={bowlF === "all" ? "Career, all formats" : `Career, ${formatLabel(bowlF)}`} right={<Seg options={fmtOptions(bowl) as any} value={bowlF} onChange={(k) => { setBowlF(k); setBowlSel(null); }} />}
          footer={<><span>Economy and average: lower is better.</span><button className="prolink" onClick={() => onGo("stats")}>Full bowling stats</button></>}>
          <div className="bat-grid">
            <div className="tiles">
              {tile(true, () => undefined, "Wickets", String(blT.w), `${blT.inn} innings bowled`, blS.map((x) => x.w))}
              {tile(false, null, "Average", fx(blT.avg), "Runs per wicket")}
              {tile(false, null, "Economy", fx(blT.econ), "Runs per over")}
              {tile(false, null, "Best figures", (bowlF === "all" ? bbi : null) ?? dash, bowlF === "all" ? "Career best" : "See top spells")}
            </div>
            <div className="chartwrap">
              <div className="chart-head"><span className="lbl">Wickets by season</span></div>
              {blS.length ? <LineChart vals={blS.map((x) => x.w)} labels={blS.map((x) => x.s)} sel={sel2} onSel={setBowlSel} fmt={(v) => `${v} wickets`} tick={(v) => Math.round(v)} ref={blT.w / blS.length} refLabel="Season average" zero aria="Wickets by season" /> : <Empty text="No season data yet." />}
              {blS[sel2] && <div className="cap"><b>{blS[sel2].s}</b>: {blS[sel2].w} wicket{blS[sel2].w === 1 ? "" : "s"} in {blS[sel2].m} matches.</div>}
            </div>
          </div>
          <div className="strip">{stat(blT.inn, "Innings bowled")}{stat(ovFromBalls(blT.balls), "Overs")}{stat(IN(blT.r), "Runs conceded")}{stat(blT.w3, "3-wicket hauls")}{stat(blT.w5, "5-wicket hauls")}{stat(blT.w ? (blT.balls / blT.w).toFixed(1) : dash, "Balls per wicket")}</div>
        </Box>
      )}

      {recent.length > 0 && (
        <Box title="Recent form" sub={`Last ${recent.length} matches, oldest to latest`} footer={<><span>Select a match row to see more.</span><button className="prolink" onClick={() => onGo("matches")}>All matches</button></>}>
          <div className="form-grid">
            <div className="form-chart">
              <FormChart items={formItems} avg={batAll.avg} />
              <div className="legend2"><span><i style={{ background: "var(--brand)" }} />Runs in the match</span><span><i style={{ background: "#fff", border: "2px solid var(--brand)", borderRadius: "50%" }} />Wickets taken</span></div>
            </div>
            <div className="fkpis">
              <div><div className="v">{fRuns}</div><div className="k">Runs</div><div className="d">{fBalls} balls faced</div></div>
              <div><div className="v">{fAvg.toFixed(1)}</div><div className="k">Average</div><div className="d">{batAll.avg ? <><Arrow d={pctDiff(fAvg, batAll.avg)} /> vs career</> : null}</div></div>
              <div><div className="v">{fSr.toFixed(1)}</div><div className="k">Strike rate</div><div className="d">{batAll.sr ? <><Arrow d={pctDiff(fSr, batAll.sr)} /> vs career</> : null}</div></div>
              <div><div className="v">{recent.filter((m) => n0(m.runsScored) >= 50).length}</div><div className="k">50+ scores</div><div className="d">in {recent.length} matches</div></div>
              <div><div className="v">{recent.reduce((a, m) => a + n0(m.wicketsTaken), 0)}</div><div className="k">Wickets</div><div className="d">in {recent.filter((m) => m.oversBowled).length} matches bowled</div></div>
              <div><div className="v">{wins}-{losses}</div><div className="k">Won-lost</div><div className="d">Team results</div></div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--line-2)" }}><RecentTable list={recent} /></div>
        </Box>
      )}

      {topInn.length > 0 && (
        <Box title="Top innings" sub="Highest scores" right={topFmts.length > 1 ? <Seg options={[["all", "All formats"], ...topFmts.map((f) => [f, formatLabel(f)] as [string, string])] as any} value={topF} onChange={setTopF} /> : undefined}>
          <div className="cards3">
            {topInn.map((x, i) => {
              const mm = x.matchMiniResponse ?? {}, b4 = n0(x.fours) * 4, b6 = n0(x.sixes) * 6, run = Math.max(0, n0(x.runsScored) - b4 - b6), r = Math.max(1, n0(x.runsScored)), rk = resultKey(x.matchResult);
              return (
                <div className={`icard ${i === 0 ? "first" : ""}`} key={i} style={{ cursor: mm.matchId ? "pointer" : undefined }} onClick={() => mm.matchId && nav(`/matches-details/${mm.matchId}`)}>
                  <div className="row1"><span className={`rk ${i === 0 ? "g" : ""}`}>#{i + 1}</span>{x.isPlayerOfMatch && <span className="potm">Player of the match</span>}<span className="fmt">{formatShort(mm.matchType)}</span></div>
                  <div className="big">{x.runsScored}{mm.notOut ? "*" : ""}<small>{x.ballsFaced} balls</small></div>
                  <div className="mini3"><div><b>{fx(x.strikeRate, 1)}</b>Strike rate</div><div><b>{n0(x.fours)}</b>Fours</div><div><b>{n0(x.sixes)}</b>Sixes</div></div>
                  <div>
                    <div className="split" title={`${Math.round(((b4 + b6) / r) * 100)}% of runs in boundaries`}><i style={{ width: `${(b4 / r) * 100}%`, background: "#29276B" }} /><i style={{ width: `${(b6 / r) * 100}%`, background: "#6E6BC4" }} /><i style={{ width: `${(run / r) * 100}%`, background: "#D9DAE6" }} /></div>
                    <div className="legend2" style={{ marginTop: 6 }}><span><i style={{ background: "#29276B" }} />{b4} in fours</span><span><i style={{ background: "#6E6BC4" }} />{b6} in sixes</span><span><i style={{ background: "#D9DAE6" }} />{run} running</span></div>
                  </div>
                  <div className="who"><b>vs {mm.opponentTeamName ?? dash}</b><span>{mm.tournamentName}</span></div>
                  <div className="foot">{fmtDate(mm.matchDate)}{x.matchResult && <span className={`res ${rk}`} style={{ marginLeft: "auto" }}>{rk}</span>}</div>
                </div>
              );
            })}
          </div>
          <div className="box-f"><span>Boundary bar: runs from fours, sixes and running.</span><button className="prolink" onClick={() => onGo("records")}>All hundreds</button></div>
        </Box>
      )}

      {topSp.length > 0 && (
        <Box title="Top spells" sub="Best bowling figures, all formats">
          <div className="cards3">
            {topSp.map((x, i) => {
              const mm = x.matchMiniResponse ?? {}, balls = ballsFromOvers(x.oversBowled), w = n0(x.wicketsTaken), rk = resultKey(x.matchResult);
              return (
                <div className={`icard ${i === 0 ? "first" : ""}`} key={i}>
                  <div className="row1"><span className={`rk ${i === 0 ? "g" : ""}`}>#{i + 1}</span><span className="fmt">{formatShort(mm.matchType)}</span></div>
                  <div className="big">{w}/{n0(x.runsConceded)}<small>{x.oversBowled} overs</small></div>
                  <div className="pips" aria-label={`${w} wickets`}>{Array.from({ length: 10 }, (_, j) => <i key={j} className={j < w ? "on" : ""} />)}</div>
                  <div className="mini3"><div><b>{fx(x.economyRate)}</b>Economy</div><div><b>{n0(x.maidenOvers)}</b>Maidens</div><div><b>{w ? (balls / w).toFixed(0) : dash}</b>Balls per wkt</div></div>
                  <div className="who"><b>vs {mm.opponentTeamName ?? dash}</b><span>{mm.tournamentName}</span></div>
                  <div className="foot">{fmtDate(mm.matchDate)}{x.matchResult && <span className={`res ${rk}`} style={{ marginLeft: "auto" }}>{rk}</span>}</div>
                </div>
              );
            })}
          </div>
        </Box>
      )}

      <div className="two">
        {fast.length > 0 && (
          <Box title="Fastest innings" sub="Minimum 15 balls" footer={batAll.sr ? <span>Line marks his career strike rate, {batAll.sr.toFixed(1)}.</span> : undefined}>
            <ul className="fast">
              {fast.map((x, i) => {
                const mm = x.matchMiniResponse ?? {}, sr = n0(x.strikeRate), cap = Math.max(200, Math.ceil(Math.max(sr, batAll.sr ?? 0) / 50) * 50);
                return (
                  <li key={i}><span className={`n ${i === 0 ? "g" : ""}`}>{i + 1}</span>
                    <div><div className="t">{x.runsScored} ({x.ballsFaced}) vs {mm.opponentTeamName ?? dash} <span>{mm.tournamentName}, {fmtDate(mm.matchDate, false)}</span></div>
                      <div className="tr"><i style={{ width: `${(sr / cap) * 100}%` }} />{batAll.sr != null && <b style={{ left: `${(batAll.sr / cap) * 100}%` }} />}</div></div>
                    <span className="sr">{sr.toFixed(1)}</span></li>
                );
              })}
            </ul>
          </Box>
        )}
        <Box title="Milestones" sub="Latest" right={<button className="link" onClick={() => onGo("records")}>All records</button>}>
          {miles.length ? (
            <ul className="tl compact">{miles.slice(0, 4).map((m, i) => (<li key={i} className={i === 0 ? "key" : ""}><span className="yr">{fmtDate(m.achievedOn)}</span><b>{m.milestone}</b><span>{m.matchMiniResponse ? `vs ${m.matchMiniResponse.opponentTeamName}, ${m.matchMiniResponse.tournamentName ?? ""}` : ""}</span></li>))}</ul>
          ) : <Empty text="No milestones yet." />}
          <div className="next" style={{ borderTop: "1px solid var(--line-2)" }}>
            {nextUps.map((n) => (<div className="it" key={n.k}><div className="top">{n.k}<em>{IN(n.v)} / {IN(n.t)}</em></div><div className="pb"><i style={{ width: `${Math.min(100, (n.v / n.t) * 100)}%` }} /></div><div className="togo">{IN(n.t - n.v)} {n.u}{n.t - n.v === 1 ? "" : "s"} to go</div></div>))}
          </div>
        </Box>
      </div>
    </>
  );
};
export default OverviewPane;
